import React, { useRef, useEffect } from 'react';
import DateSeparator from './DateSeparator';
import UnreadDivider from './UnreadDivider';
import {
    groupMessagesByDate,
    getFirstUnreadMessageIndex,
} from './messageUtils';
import '../../styles-wa/MessageGroup.css';

/**
 * MessageGroup Component
 *
 * WhatsApp-exact conversation opening behavior:
 *  - Has unread messages Ã¢— —™ scroll so the UnreadDivider is centered in the viewport
 *  - No unread messages   Ã¢— —™ scroll to the very bottom (latest message)
 *
 * Uses scrollIntoView for accurate positioning regardless of message heights.
 */
const MessageGroup = ({
    messages = [],
    renderMessage,
    onScrollToUnread,
    containerRef,
    shouldAutoScroll = true,
    conversationId = null,
    unreadAnchorId = null,
    onInitialScrollComplete,
    typingContact = null,
    savedScrollTop = null,
}) => {
    const unreadDividerRef = useRef(null);
    const bottomAnchorRef = useRef(null);
    const hasScrolledRef = useRef(false);
    const prevConversationIdRef = useRef(null);

    // Ã¢—â‚¬Ã¢—â‚¬ Reset scroll-guard whenever conversation changes Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬
    useEffect(() => {
        if (conversationId !== prevConversationIdRef.current) {
            hasScrolledRef.current = false;
            prevConversationIdRef.current = conversationId;
        }
    }, [conversationId]);

    // Ã¢—â‚¬Ã¢—â‚¬ Initial scroll on conversation open Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬
    useEffect(() => {
        if (!shouldAutoScroll) return;
        if (hasScrolledRef.current) return;
        if (!messages || messages.length === 0) return;

        const scrollContainer = containerRef?.current;
        if (!scrollContainer) return;

        hasScrolledRef.current = true;

        // 1. If we have a saved scroll position, restore it instantly!
        if (savedScrollTop !== null && savedScrollTop !== undefined && savedScrollTop > 0) {
            scrollContainer.scrollTop = savedScrollTop;
            onInitialScrollComplete?.('restored');
            return;
        }

        // 2. Otherwise, check for unread messages
        const hasUnread = unreadAnchorId
            ? messages.some(m => String(m.id) === String(unreadAnchorId))
            : getFirstUnreadMessageIndex(messages) !== -1;

        if (hasUnread && unreadDividerRef.current) {
            // First jump to bottom instantly to avoid the scroll-from-top visual
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
            // Then instantly scroll to center the first unread message
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (unreadDividerRef.current) {
                        unreadDividerRef.current.scrollIntoView({
                            behavior: 'instant',
                            block: 'center',
                        });
                    }
                    onScrollToUnread?.();
                    onInitialScrollComplete?.('unread');
                });
            });
        } else {
            // No unread: jump INSTANTLY to the bottom Ã¢â‚¬— no animation at all
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
            if (bottomAnchorRef.current) {
                bottomAnchorRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
            }
            onInitialScrollComplete?.('bottom');
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId, messages.length > 0 ? 1 : 0, unreadAnchorId, savedScrollTop]);

    // Ã¢—â‚¬Ã¢—â‚¬ Compute groupings Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬
    const groupedMessages = groupMessagesByDate(messages);

    const firstUnreadGlobalIndex = unreadAnchorId
        ? messages.findIndex(m => String(m.id) === String(unreadAnchorId))
        : getFirstUnreadMessageIndex(messages);

    const hasUnread = firstUnreadGlobalIndex !== -1;
    const unreadCount = hasUnread
        ? messages.slice(firstUnreadGlobalIndex).filter(m =>
            (m.direction === 'INCOMING' || m.sender === 'customer') &&
            !m.read && !m.is_read && !m.read_at
        ).length || 1
        : 0;

    // Ã¢—â‚¬Ã¢—â‚¬ Empty state Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬
    if (messages.length === 0) {
        return (
            <div className="message-group-empty">
                <div className="message-group-empty-icon">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                </div>
                <p>No messages yet</p>
                <span>Start the conversation!</span>
            </div>
        );
    }

    // Ã¢—â‚¬Ã¢—â‚¬ Render grouped messages Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬Ã¢—â‚¬
    return (
        <div className="message-group-container">
            {groupedMessages.map((group) => {
                // Find the first unread index within this date-group
                const groupFirstUnreadIdx = group.messages.findIndex((msg) => {
                    if (unreadAnchorId) return String(msg.id) === String(unreadAnchorId);
                    const isIncoming = msg.direction === 'INCOMING' || msg.sender === 'customer';
                    const isUnread = !msg.read && !msg.is_read && !msg.read_at;
                    return isIncoming && isUnread;
                });
                const groupHasUnread = groupFirstUnreadIdx !== -1;

                return (
                    <div key={group.dateGroup} className="message-date-group">
                        <DateSeparator dateLabel={group.dateGroup} />

                        {group.messages.map((message, messageIndex) => {
                            const isFirstUnread = groupHasUnread && messageIndex === groupFirstUnreadIdx;
                            const isIncoming = message.direction === 'INCOMING' || message.sender === 'customer' || message.sender === 'received';
                            const isRead = Boolean(message.read || message.is_read || message.read_at || String(message.status || '').toLowerCase() === 'read');
                            const isUnreadIncoming = isIncoming && !isRead;

                            return (
                                <div
                                    key={message.id}
                                    className={`message-with-divider ${isUnreadIncoming ? 'unread-incoming-message' : ''}`}
                                    data-message-id={message.id}
                                >
                                    {isFirstUnread && (
                                        <div ref={unreadDividerRef}>
                                            <UnreadDivider unreadCount={unreadCount} />
                                        </div>
                                    )}
                                    {renderMessage(message)}
                                </div>
                            );
                        })}
                    </div>
                );
            })}

            {/* Typing indicator */}
            {typingContact && (
                <div className="typing-indicator-wrapper">
                    <div className="typing-bubble">
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                    </div>
                </div>
            )}

            {/* Invisible anchor at the very bottom for scroll-to-bottom */}
            <div ref={bottomAnchorRef} style={{ height: 1, flexShrink: 0 }} aria-hidden="true" />
        </div>
    );
};

export default MessageGroup;



